import { Evaluation } from '@/models/evaluations';

export type QueryToken = {
  text: string;
  type:
    | 'text'
    | 'operator'
    | 'negatedOperator'
    | 'negation'
    | 'paren'
    | 'groupParen'
    | 'space';
};
type KeywordGroupData = { id: number; keywords: string[] };

const SPACE: QueryToken = { text: ' ', type: 'space' };
const quote = (s: string) => `"${s}"`;

const isNegated = (el: Evaluation.QueryElement): boolean =>
  el.type !== 'operator' && !!el.isNegated;

const operandTokens = (
  el: Evaluation.QueryElement,
  keywordGroups: KeywordGroupData[],
  includeOwnNegation: boolean
): QueryToken[] => {
  const prefix: QueryToken[] =
    includeOwnNegation && isNegated(el)
      ? [{ text: 'NOT', type: 'negation' }, SPACE]
      : [];

  if (el.type === 'keyword') {
    return [...prefix, { text: quote(el.value), type: 'text' }];
  }

  if (el.type === 'keywordGroup') {
    const group = keywordGroups.find((g) => g.id === el.id);
    const inner: QueryToken[] = [];
    if (group?.keywords?.length) {
      group.keywords.forEach((kw, i) => {
        if (i > 0)
          inner.push(SPACE, { text: el.operator, type: 'operator' }, SPACE);
        inner.push({ text: quote(kw), type: 'text' });
      });
    } else {
      inner.push({ text: `Group #${el.id}`, type: 'text' });
    }
    return [
      ...prefix,
      { text: '(', type: 'paren' },
      ...inner,
      { text: ')', type: 'paren' }
    ];
  }

  if (el.type === 'logicalGroup') {
    const inner = elementsToTokens(el.elements, keywordGroups);
    return [
      ...prefix,
      { text: '((', type: 'groupParen' },
      ...inner,
      { text: '))', type: 'groupParen' }
    ];
  }

  throw new Error(`Unexpected element type: ${el.type}`);
};

const elementsToTokens = (
  elements: Evaluation.QueryElement[],
  keywordGroups: KeywordGroupData[]
): QueryToken[] => {
  const tokens: QueryToken[] = [];

  elements.forEach((el, i) => {
    if (i > 0) tokens.push(SPACE);

    if (el.type === 'operator') {
      const next = elements[i + 1];
      const mergesWithNot =
        !!next && next.type !== 'operator' && isNegated(next);
      tokens.push({
        text: mergesWithNot ? `${el.value} NOT` : el.value,
        type: mergesWithNot ? 'negatedOperator' : 'operator'
      });
      return;
    }

    const prevWasOperator = i > 0 && elements[i - 1].type === 'operator';
    tokens.push(...operandTokens(el, keywordGroups, !prevWasOperator));
  });

  return tokens;
};

export const queryToTokens = (
  root: Evaluation.LogicalGroupElement | null,
  keywordGroups: KeywordGroupData[]
): QueryToken[] =>
  root?.elements.length
    ? elementsToTokens(root.elements, keywordGroups)
    : [{ text: 'No query defined', type: 'text' }];
